// Vite-driven discovery of bundled theme packs at <workspace>/themes/<key>/.
// `eager: true` loads everything synchronously at module init so the
// registry is hot before any UI renders. Touching this comment forces
// Vite to re-evaluate the glob when a new theme folder (e.g. futuristic) is added.
//
// `query: '?url'` returns the resolved (cache-busted) asset URL Vite
// serves at runtime; `import: 'default'` extracts the default export.
//
// Glob paths are relative to THIS file, not the Vite project root, because
// the workspace `themes/` directory lives outside `apps/web/`. From
// `apps/web/src/theme/`, the workspace `themes/` is three levels up.

import { BundledThemeRegistry, type ThemePackSource, type ThemeRegistry } from '@classy/theme-sdk';

interface JsonModule {
  default: unknown;
}

const manifestModules = import.meta.glob<JsonModule>('../../../../themes/*/theme-pack.json', {
  eager: true,
});

const assetUrls = import.meta.glob<string>('../../../../themes/*/**/*.{png,jpg,webp,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
});

const KEY_FROM_PATH = /\/themes\/([^/]+)\//;

function buildSources(): ThemePackSource[] {
  const sources = new Map<string, ThemePackSource>();
  for (const [path, mod] of Object.entries(manifestModules)) {
    const key = path.match(KEY_FROM_PATH)?.[1];
    if (!key) continue;
    sources.set(key, { manifestRaw: mod.default, assets: {} });
  }

  for (const [path, url] of Object.entries(assetUrls)) {
    const key = path.match(KEY_FROM_PATH)?.[1];
    if (!key) continue;
    const source = sources.get(key);
    if (!source) continue;
    // Strip everything up through `/themes/<key>/` so the asset path is
    // pack-relative (`backgrounds/home.png`), independent of however many
    // `../` segments the absolute glob path picked up.
    const relative = path.replace(new RegExp(`^.*/themes/${key}/`), '');
    source.assets[relative] = url;
  }

  return Array.from(sources.values());
}

export function createBundledThemeRegistry(): ThemeRegistry {
  return new BundledThemeRegistry(buildSources());
}
